import React, { useEffect, useState } from "react";
import { Button, Dialog, Divider, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import { branchTimezone, setTheAddedDiscountList } from "../../../utils/summaryCalculation";
import useAuth from "../../../components/hooks/useAuth";
import { validateMin } from "../../../utils/validateMin";
import useConfirm from "../../../components/hooks/useConfirm";
import { useTheme } from "../../../components/hooks/useTheme";
import colorConfigs from "../../../configs/colorConfigs";
import { updateDiscount } from "../../../services/rental";
import Cookies from "js-cookie";
import useTenant from "../../../components/hooks/useTenant";

type Props = {
  refreshTheSummary: () => void;
  formattedCreatedAt: string;
  discountOptions: any[];
  appliedDiscountList: any[];
  isCompletedOrCancelled: boolean;
  setPleaseWait: (value: boolean) => void;
  reservationId: number;
  summaryData: any;
};

export default function AddDiscount({
  refreshTheSummary,
  formattedCreatedAt,
  discountOptions,
  appliedDiscountList,
  isCompletedOrCancelled,
  setPleaseWait,
  reservationId,
  summaryData
}: Props) {
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { theme, themeStyles } = useTheme();
  const { auth }: any = useAuth();
  const { handleTenantError }: any = useTenant();
  const { setConfirmPopup, setData: setConfirmData }: any = useConfirm();
  const [isAddDiscount, setIsAddDiscount] = useState<boolean>(false);
  const [couponCode, setCouponCode] = useState<number>(0);
  const [manualDiscount, setManualDiscount] = useState<string>("");
  const [isManualDiscountInAPercentage, setIsManualDiscountInAPercentage] = useState<boolean>(false);
  const UpdateTimezone = branchTimezone(formattedCreatedAt);
  const closeIconFill = theme === "dark" ? "fill-[#E4E7EC]" : "fill-[#1D2939]";

  useEffect(() => {
    setCouponCode(appliedDiscountList[0]?.discountId || 0);
    if (summaryData?.reopenedAt) {
      let suggestedManualDiscountAmount = "";
      if (summaryData?.outstandingBalance > 0) {
        let discountAmount = 0;
        let taxPercentageValue = 0;

        if (summaryData?.locationFeePercentage > 0) {
          taxPercentageValue += summaryData?.locationFeePercentage;
        }

        if (summaryData?.taxPercentage > 0) {
          taxPercentageValue += summaryData?.taxPercentage;
        }

        if (summaryData?.governmentTaxPercentage > 0) {
          taxPercentageValue += summaryData?.governmentTaxPercentage;
        }

        if (taxPercentageValue > 0) {
          discountAmount = Number(((summaryData?.outstandingBalance * 100) / (100 + taxPercentageValue))?.toFixed(2));
        }

        suggestedManualDiscountAmount = discountAmount?.toString();
      }

      setManualDiscount(suggestedManualDiscountAmount || "");
      setIsManualDiscountInAPercentage(false);
    } else {
      setManualDiscount(appliedDiscountList[1]?.discountAmount || "");
      setIsManualDiscountInAPercentage(appliedDiscountList[1]?.discountType !== "Amount");
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedDiscountList])

  //Discount
  const handleDiscount = () => {
    if (!isCompletedOrCancelled) {
      setPleaseWait(true);

      const updatedAppliedDiscountList = setTheAddedDiscountList(couponCode, manualDiscount, isManualDiscountInAPercentage, discountOptions, appliedDiscountList);

      const processDiscounts = (): Promise<{
        applyDiscounts: any[],
        updateDiscounts: any[],
        deleteDiscountIds: any[]
      }> => {
        return new Promise((resolve) => {
          const applyDiscounts: any[] = [];
          const updateDiscounts: any[] = [];
          const deleteDiscountIds: any[] = [];

          const newDiscountList = [...updatedAppliedDiscountList];

          appliedDiscountList?.forEach((item: any, index: number) => {
            if (index === 0) {
              if (item?.discountId === 0 && newDiscountList[0]?.discountId !== 0) {
                applyDiscounts.push({
                  id: 0,
                  reservationId: reservationId,
                  discountId: newDiscountList[0]?.discountId,
                  discountDefinition: newDiscountList[0]?.discountDefinition,
                  discountType: newDiscountList[0]?.discountType,
                  discountAmount: newDiscountList[0]?.discountAmount,
                  manualDiscount: false,
                  supportingDocuments: false,
                  dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                  createdAt: new Date(UpdateTimezone).toLocaleString(),
                  createdBy: auth.givenname
                });

                newDiscountList[0] = {
                  ...newDiscountList[0],
                  discountApplied: true
                };
              } else if (
                item?.discountId !== 0 &&
                newDiscountList[0]?.discountId !== 0 &&
                item?.discountId !== newDiscountList[0]?.discountId
              ) {
                updateDiscounts.push({
                  id: newDiscountList[0]?.id,
                  reservationId: reservationId,
                  discountId: newDiscountList[0]?.discountId,
                  discountDefinition: newDiscountList[0]?.discountDefinition,
                  discountType: newDiscountList[0]?.discountType,
                  discountAmount: newDiscountList[0]?.discountAmount,
                  manualDiscount: false,
                  supportingDocuments: false,
                  dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                  createdAt: new Date(UpdateTimezone).toLocaleString(),
                  createdBy: auth.givenname
                });

                newDiscountList[0] = {
                  ...newDiscountList[0],
                  discountApplied: true
                };
              } else if (item?.discountId !== 0 && newDiscountList[0]?.discountId === 0) {
                deleteDiscountIds.push(appliedDiscountList[0]?.id);

                newDiscountList[0] = {
                  ...newDiscountList[0],
                  discountApplied: false
                };
              }
            }

            if (index === 1) {
              if (!item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                applyDiscounts.push({
                  id: 0,
                  reservationId: reservationId,
                  discountId: newDiscountList[1]?.discountId,
                  discountDefinition: newDiscountList[1]?.discountDefinition,
                  discountType: newDiscountList[1]?.discountType,
                  discountAmount: newDiscountList[1]?.discountAmount,
                  manualDiscount: true,
                  supportingDocuments: false,
                  dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                  createdAt: new Date(UpdateTimezone).toLocaleString(),
                  createdBy: auth.givenname
                });

                newDiscountList[1] = {
                  ...newDiscountList[1],
                  discountApplied: true
                };
              } else if (item?.discountApplied && newDiscountList[1]?.discountAmount > 0) {
                updateDiscounts.push({
                  id: newDiscountList[1]?.id,
                  reservationId: reservationId,
                  discountId: 0,
                  discountDefinition: newDiscountList[1]?.discountDefinition,
                  discountType: newDiscountList[1]?.discountType,
                  discountAmount: newDiscountList[1]?.discountAmount,
                  manualDiscount: true,
                  supportingDocuments: false,
                  dateofDiscount: new Date(UpdateTimezone).toLocaleString(),
                  createdAt: new Date(UpdateTimezone).toLocaleString(),
                  createdBy: auth.givenname
                });

                newDiscountList[1] = {
                  ...newDiscountList[1],
                  discountApplied: true
                };
              } else if (item?.discountApplied && newDiscountList[1]?.discountAmount <= 0) {
                deleteDiscountIds.push(appliedDiscountList[1]?.id);

                newDiscountList[1] = {
                  ...newDiscountList[1],
                  discountApplied: false
                };
              }
            }
          });

          resolve({
            applyDiscounts,
            updateDiscounts,
            deleteDiscountIds
          });
        });
      };

      processDiscounts().then(({ applyDiscounts, updateDiscounts, deleteDiscountIds }) => {
        updateDiscount(
          {
            reservationDiscount: [...applyDiscounts, ...updateDiscounts],
            reservationDiscountIds: deleteDiscountIds
          },
          reservationId,
          auth?.id,
          tenantId
        )
          .then(() => {
            refreshTheSummary();
            setIsAddDiscount(false);
            handleTenantError(null);
          })
          .catch(error => {
            setPleaseWait(false)
            handleTenantError(error);
          });
      });
    }
  }

  const handleDelete = async (id: number) => {
    if (!isCompletedOrCancelled) {
      setConfirmData({
        title: 'Delete Discoount',
        summary: "Are you sure you want to remove this discount?",
        confirmBtn: 'Yes',
        isConfirm: async (callback: any) => {
          setPleaseWait(true);

          updateDiscount(
            {
              reservationDiscount: [],
              reservationDiscountIds: [id]
            },
            reservationId,
            auth?.id,
            tenantId
          )
            .then(() => {
              refreshTheSummary()
              handleTenantError(null);
            })
            .catch(error => {
              setPleaseWait(false)
              handleTenantError(error);
            });

          callback();
        }
      });

      setConfirmPopup(true);
    }
  };

  return (
    <div className="my-4">
      <span className={`text-[14px] font-semibold inline-block mb-2 ${themeStyles.main.textHeadingColor}`}>
        Discounts
      </span>

      <Divider className={`w-full ${themeStyles.divider.bg}`} />

      <div className="w-full my-2">
        <table className="min-w-full">
          <tbody className="text-left">
            <tr className={`border-b ${themeStyles.main.borderColor}`}>
              <th className={`w-[8%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>#</th>
              <th className={`w-[32%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Discount definition</th>
              <th className={`w-[26%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Discount Type</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Discount</th>
              <th className={`w-[10%] text-[12px] font-semibold mb-2 py-2 ${themeStyles.table.tailwindHeaderColor} ${themeStyles.table.headerBg}`}>Delete</th>
            </tr>

            {
              appliedDiscountList?.filter((item: any) => item?.discountApplied)
                ?.map((item: any, index: number) => {
                  return (
                    <tr key={index} className={`border-b ${themeStyles.main.borderColor}`}>
                      <td className="py-2 w-[8%]">
                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.id}</span>
                      </td>

                      <td className="py-2 w-[32%]">
                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.discountDefinition}</span>
                      </td>

                      <td className="w-[26%]">
                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>{item?.discountType}</span>
                      </td>

                      <td className="py-2 w-[10%]">
                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>
                          {item?.discountType === "Amount" ? "$" : ""}
                          {item?.discountAmount?.toFixed(2)}
                          {item?.discountType !== "Amount" ? "%" : ""}
                        </span>
                      </td>

                      <td className="py-2 w-[10%]">
                        <span className={`text-xs font-medium ${themeStyles.main.textHeadingColor}`}>
                          <RemoveCircleOutlinedIcon
                            sx={{
                              fontSize: "12px",
                              color: "gray",
                              cursor: "pointer",
                              "&: hover": {
                                color: "red",
                              },
                            }}
                            onClick={() =>
                              handleDelete(item?.id)
                            }
                          />
                        </span>
                      </td>
                    </tr>
                  );
                })
            }
          </tbody>
        </table>

        <div className="w-full flex items-center gap-2.5 mt-4">
          <Button variant="contained"
            sx={{
              backgroundColor: colorConfigs.btn.main.bg,
              fontSize: "10px"
            }}
            onClick={() => {
              if (!isCompletedOrCancelled)
                setIsAddDiscount(true)
            }}
          >
            Add Discount
          </Button>
        </div>
      </div>

      <Dialog
        open={isAddDiscount}
        maxWidth="md"
        fullScreen={false}
        sx={{
          '& .MuiDialog-paper': {
            maxWidth: '600px',
            width: '100%',
            borderRadius: '16px',
          },
        }}
      >
        <div className={`px-[15px] py-[15px] h-full ${themeStyles.main.tailwindSectionBg}`}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 320.591 320.591"
            className={`w-3 cursor-pointer float-right ${closeIconFill}`}
            onClick={() => setIsAddDiscount(false)}
          >
            <path d="M30.391 318.583a30.37 30.37 0 0 1-21.56-7.288c-11.774-11.844-11.774-30.973 0-42.817L266.643 10.665c12.246-11.459 31.462-10.822 42.921 1.424 10.362 11.074 10.966 28.095 1.414 39.875L51.647 311.295a30.366 30.366 0 0 1-21.256 7.288z" />
            <path d="M287.9 318.583a30.37 30.37 0 0 1-21.257-8.806L8.83 51.963C-2.078 39.225-.595 20.055 12.143 9.146c11.369-9.736 28.136-9.736 39.504 0l259.331 257.813c12.243 11.462 12.876 30.679 1.414 42.922-.456.487-.927.958-1.414 1.414a30.368 30.368 0 0 1-23.078 7.288z" />
          </svg>
          <div className={`w-full bg-transparent rounded-lg p-[10px] flex flex-col gap-2.51`}>
            <div className="w-full mt-2">
              <div className={`w-full`}>
                <div className={`flex justify-between items-center p-2 border-b ${themeStyles.main.borderColor}`}>
                  <div className="flex items-center gap-1.5">
                    <h2 className={`text-lg font-semibold ${themeStyles.main.textHeadingColor}`}>Discount</h2>
                  </div>
                </div>

                <div className="mt-2 p-2">
                  <div className="w-full">
                    <form>
                      <div className="w-full">
                        <div className="grid grid-cols-2 gap-6">
                          <div className="space-y-4">
                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Coupon Code</label>
                              <select
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={couponCode}
                                onChange={(e) => {
                                  setCouponCode(parseInt(e.target.value));
                                }}
                              >
                                <option className={`${themeStyles.main.tailwindSectionBg}`} value={0}>Select...</option>
                                {
                                  discountOptions?.map((item: any) => {
                                    return <option className={`${themeStyles.main.tailwindSectionBg}`} key={item.id} value={item.id}>{item.name}</option>;
                                  })
                                }
                              </select>
                            </div>
                          </div>

                          <div className="space-y-4">
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mt-[1.25rem]">
                          <div className="space-y-4">
                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Manual Discount({isManualDiscountInAPercentage ? "%" : "$"})</label>
                              <input
                                type="number"
                                min={0}
                                className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
                                value={manualDiscount}
                                defaultValue={0.00}
                                onChange={(e) => {
                                  setManualDiscount(validateMin(e.target.value, 0).toString());
                                }}
                              />
                            </div>
                          </div>

                          <div className="space-y-4">
                            <div>
                              <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>Manual Discount is a Percentage?</label>
                              <RadioGroup
                                row
                                value={isManualDiscountInAPercentage ? "Yes" : "No"}
                                onChange={(e) =>
                                  setIsManualDiscountInAPercentage(
                                    e.target.value === "Yes" ? true : false
                                  )
                                }
                              >
                                <FormControlLabel
                                  value="Yes"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                          color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  }
                                  label="Yes"
                                  sx={{
                                    color: themeStyles.form.labelTextColor,
                                    fontSize: "12px"
                                  }}
                                />

                                <FormControlLabel
                                  value="No"
                                  control={
                                    <Radio
                                      sx={{
                                        color: "gray",
                                        "&.Mui-checked": {
                                          color: "#167AC3",
                                        },
                                        '& .MuiSvgIcon-root': {
                                          fontSize: 14,
                                        },
                                      }}
                                    />
                                  }
                                  label="No"
                                  sx={{
                                    color: themeStyles.form.labelTextColor,
                                  }}
                                />
                              </RadioGroup>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-4 mt-6">
                        <Button variant="contained"
                          sx={{
                            backgroundColor: colorConfigs.btn.main.bg
                          }}
                          type="button"
                          onClick={() => {
                            setTimeout(() => handleDiscount(), 0);
                          }}
                        >
                          Save
                        </Button>

                        <Button
                          variant="contained"
                          sx={{
                            backgroundColor: colorConfigs.btn.cancel.bg,
                            border: colorConfigs.btn.cancel.border,
                            color: colorConfigs.btn.cancel.color
                          }}
                          type="button"
                          onClick={() => setIsAddDiscount(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
